'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Navbar from '@/components/physio/Navbar';
import Footer from '@/components/physio/Footer';
import WhatsAppButton from '@/components/physio/WhatsAppButton';

interface Service {
  id: string;
  name: string;
  shortDesc: string;
  longDesc: string;
  treatments: string[];
  duration: string;
  sessions: string;
  image: string;
  imageAlt: string;
  badge?: string;
  category: string[];
}

const services: Service[] = [
{
  id: 'orthopedic',
  name: 'Orthopedic Rehabilitation',
  shortDesc: 'Joint replacements, fractures, and musculoskeletal injuries.',
  longDesc: 'Comprehensive rehabilitation following joint replacement surgery, fractures, ligament repairs, and chronic musculoskeletal conditions. Your therapist designs a personalised programme that progresses week by week.',
  treatments: ['Joint Replacement Rehab (Knee/Hip)', 'Fracture Rehabilitation', 'Ligament Injury Rehabilitation', 'Sports Injury Rehabilitation', 'Post-Surgical Rehabilitation'],
  duration: '60 min sessions',
  sessions: 'From 6 sessions',
  image: '/assets/images/orthopedics-1785953141994.jpeg',
  imageAlt: 'Physiotherapist performing knee rehabilitation exercises with a patient at home, professional setting',
  badge: 'Most Popular',
  category: ['All', 'Musculoskeletal', 'Rehabilitation']
},
{
  id: 'neurological',
  name: 'Neurological Rehab',
  shortDesc: 'Specialist care for stroke, spinal cord injury, and neurological conditions.',
  longDesc: 'Our specialist neurological physiotherapists use evidence-based approaches to improve movement, balance, and function after neurological events. Specialist home visits for patients who cannot travel.',
  treatments: ['Stroke Rehabilitation', 'Spinal Cord Injury Management', 'Multiple Sclerosis', 'Facial Palsy', 'Gait Training'],
  duration: '60–90 min',
  sessions: 'Ongoing programme',
  image: '/assets/images/neuro-1785953141919.jpeg',
  imageAlt: 'Neurological physiotherapist supporting stroke patient with walking rehabilitation at home',
  category: ['All', 'Neurological']
},
{
  id: 'geriatric',
  name: 'Geriatric Physiotherapy',
  shortDesc: 'Gentle, patient-centred care for elderly patients.',
  longDesc: 'Comprehensive geriatric physiotherapy addressing the unique needs of elderly patients. We focus on improving quality of life, independence, and safety at home.',
  treatments: ['Fall Prevention', 'Balance Training', 'Strengthening Exercises', 'Mobility Improvement', 'Arthritis Management', 'Osteoporosis Exercise Programme'],
  duration: '45–60 min',
  sessions: 'From 6 sessions',
  image: '/assets/images/geriatric-1785953142319.jpeg',
  imageAlt: 'Elderly patient doing balance exercises with physiotherapist in home environment, caring and supportive setting',
  category: ['All', 'Rehabilitation', 'Wellness']
},
{
  id: 'cardio',
  name: 'Cardio Pulmonary Rehabilitation',
  shortDesc: 'Breathing and chest physiotherapy for respiratory conditions.',
  longDesc: 'Specialised cardiopulmonary rehabilitation to improve breathing capacity, manage respiratory conditions, and enhance overall cardiovascular fitness through targeted physiotherapy interventions.',
  treatments: ['Breathing Exercises', 'Chest Physiotherapy', 'COPD Rehab', 'Incentive Spirometry Training'],
  duration: '45–60 min',
  sessions: 'From 6 sessions',
  image: '/assets/images/cardio_pul-1785953142887.jpeg',
  imageAlt: 'Physiotherapist performing chest physiotherapy and breathing exercises with patient at home',
  category: ['All', 'Rehabilitation']
},
{
  id: 'post-surgery',
  name: 'Post-Surgery Rehabilitation',
  shortDesc: 'Structured recovery coordinated with your surgical team.',
  longDesc: 'We liaise directly with your surgeon to ensure your rehabilitation protocol aligns with surgical findings. Phased recovery programmes that restore function safely and progressively.',
  treatments: ['Hip Replacement', 'Knee Replacement', 'Rotator Cuff Repair', 'Spinal Surgery', 'Post-Surgical Rehabilitation'],
  duration: '60 min',
  sessions: 'From 8 sessions',
  image: '/assets/images/post_surgical-1785953142890.jpeg',
  imageAlt: 'Physiotherapist guiding post-surgery patient through gentle hip mobility exercises at home, supportive care',
  category: ['All', 'Rehabilitation']
},
{
  id: 'womens-health',
  name: "Women's Health Physiotherapy",
  shortDesc: 'Specialised physiotherapy for women at all life stages.',
  longDesc: "Dedicated women's health physiotherapy addressing the unique physiological needs of women, from pregnancy through postpartum recovery and beyond.",
  treatments: ['Postpartum Rehabilitation', 'Diastasis Recti Exercises', 'Pelvic Floor Strengthening', 'Pregnancy Exercise Guidance'],
  duration: '45–60 min',
  sessions: 'From 4 sessions',
  image: '/assets/images/womens_health-1785953400588.jpeg',
  imageAlt: "Women's health physiotherapist providing postpartum rehabilitation and pelvic floor therapy at home",
  category: ['All', 'Wellness']
},
{
  id: 'pediatric',
  name: 'Pediatric Physiotherapy',
  shortDesc: 'Specialised physiotherapy for children and developmental needs.',
  longDesc: 'Child-friendly physiotherapy addressing developmental delays, neurological conditions, and musculoskeletal issues in children. Our therapists create engaging, age-appropriate treatment programmes.',
  treatments: ['Developmental Delay', 'Cerebral Palsy', 'Torticollis', 'Balance and Coordination Training for Children'],
  duration: '45–60 min',
  sessions: 'From 6 sessions',
  image: '/assets/images/pediatric_physiotherapy-1785953401151.jpeg',
  imageAlt: 'Pediatric physiotherapist working with child on balance and coordination exercises in a home setting',
  category: ['All', 'Neurological', 'Rehabilitation']
},
{
  id: 'exercise',
  name: 'Exercise & Fitness',
  shortDesc: 'Therapeutic exercise programmes for health and fitness goals.',
  longDesc: 'Evidence-based therapeutic exercise programmes designed to improve strength, flexibility, and overall fitness. Our physiotherapists create personalised programmes tailored to your specific goals and health status.',
  treatments: ['Therapeutic Exercise Programs', 'Flexibility Training', 'Strength Training', 'Core Strengthening', 'Weight Management Exercise Programs'],
  duration: '45–60 min',
  sessions: 'From 4 sessions',
  image: 'https://images.unsplash.com/photo-1594737625785-a6cbdabd333c?w=600&q=80',
  imageAlt: 'Physiotherapist guiding patient through therapeutic exercise programme at home, strength and flexibility training',
  category: ['All', 'Wellness']
},
{
  id: 'functional',
  name: 'Functional Rehabilitation',
  shortDesc: 'Restoring everyday movement and independence.',
  longDesc: 'Comprehensive functional rehabilitation focused on restoring the ability to perform everyday activities safely and independently. We work on the specific movements and tasks that matter most to you.',
  treatments: ['Bed Mobility Training', 'Transfer Training', 'Stair Climbing Training', 'Walking Training', 'Activities of Daily Living (ADL) Training'],
  duration: '60 min',
  sessions: 'From 6 sessions',
  image: '/assets/images/functional-1785953400896.jpeg',
  imageAlt: 'Physiotherapist assisting patient with functional mobility training and daily activity rehabilitation at home',
  category: ['All', 'Rehabilitation']
},
{
  id: 'ergonomic',
  name: 'Ergonomic & Lifestyle Services',
  shortDesc: 'Posture correction and work-from-home ergonomic assessment.',
  longDesc: 'Comprehensive ergonomic assessment and lifestyle modification services to prevent injury, reduce pain, and improve posture. Ideal for those working from home or experiencing posture-related discomfort.',
  treatments: ['Posture Correction', 'Work-from-Home Ergonomic Assessment', 'Home Exercise Program'],
  duration: '60 min',
  sessions: 'From 2 sessions',
  image: '/assets/images/ergonomics-1785953401209.jpeg',
  imageAlt: 'Physiotherapist conducting ergonomic assessment for work-from-home setup, posture correction guidance',
  category: ['All', 'Wellness']
},
{
  id: 'pain',
  name: 'Pain Management',
  shortDesc: 'Comprehensive pain relief for musculoskeletal conditions.',
  longDesc: 'Targeted physiotherapy interventions for a wide range of pain conditions. Our therapists use evidence-based techniques including manual therapy, therapeutic exercises, and patient education to provide lasting pain relief.',
  treatments: ['Neck Pain', 'Low Back Pain', 'Shoulder Pain (Frozen Shoulder, Rotator Cuff)', 'Knee Pain (Osteoarthritis)', 'Hip Pain', 'Ankle and Foot Pain', 'Tennis Elbow / Golfer\'s Elbow', 'Plantar Fasciitis'],
  duration: '45–60 min',
  sessions: 'From 4 sessions',
  image: '/assets/images/pain_management-1785953401149.jpeg',
  imageAlt: 'Physiotherapist applying manual therapy for pain management on patient at home, professional treatment',
  category: ['All', 'Musculoskeletal']
},
{
  id: 'back-neck',
  name: 'Back & Neck Pain',
  shortDesc: 'Acute and chronic spinal pain, disc issues, and postural problems.',
  longDesc: 'Manual therapy, targeted exercises, and postural correction for lower back pain, cervical pain, disc herniation, and sciatica. The leading cause of disability — we treat it at home.',
  treatments: ['Lower Back Pain', 'Sciatica', 'Disc Herniation', 'Neck Pain', 'Postural Issues'],
  duration: '45–60 min',
  sessions: 'From 4 sessions',
  image: '/assets/images/back_n_neck_pain-1785953583256.jpeg',
  imageAlt: 'Physiotherapist performing back assessment and manual therapy on patient at home',
  category: ['All', 'Musculoskeletal']
}];


const categories = ['All', 'Musculoskeletal', 'Neurological', 'Rehabilitation', 'Wellness'];

export default function ServicesPage() {
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredServices = services.filter((s) => s.category.includes(activeCategory));

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="pt-32 pb-16 overflow-hidden" style={{ backgroundColor: 'var(--background)' }}>
          <div className="mx-auto max-w-6xl px-6">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Left */}
              <div className="space-y-6">
                <span className="service-tag">All Services</span>
                <h1 className="text-hero-xl font-display font-bold" style={{ color: 'var(--foreground)' }}>
                  Every treatment,{' '}
                  <span className="font-display italic text-gradient-teal">at your home</span>
                </h1>
                <p className="text-lg leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                  Browse our full range of physiotherapy services — from acute injuries to long-term neurological care. All delivered by CMT-certified specialists across Kochi, Kerala.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link href="/contact" className="btn-primary text-sm px-7 py-3.5">
                    Book a Session
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                    </svg>
                  </Link>
                  <a href="tel:+919497815224" className="btn-secondary text-sm px-7 py-3.5 inline-flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
                    </svg>
                    Call Now
                  </a>
                </div>
              </div>

              {/* Right - 3 images */}
              <div className="hidden lg:grid grid-cols-3 gap-3 items-start">
                <div className="relative aspect-[3/4] rounded-2xl overflow-hidden shadow-md" style={{ border: '1px solid var(--border)' }}>
                  <Image
                    src="/assets/images/WhatsApp_Image_2026-08-06_at_9.32.18_AM-1785989820166.jpeg"
                    alt="Physiotherapist providing home physiotherapy session to patient"
                    fill
                    className="object-cover"
                    sizes="(max-width: 1024px) 0vw, 15vw"
                  />
                </div>
                <div className="relative aspect-[3/4] rounded-2xl overflow-hidden shadow-md mt-6" style={{ border: '1px solid var(--border)' }}>
                  <Image
                    src="/assets/images/WhatsApp_Image_2026-08-06_at_9.31.46_AM-1785989820190.jpeg"
                    alt="Physiotherapist performing rehabilitation exercises with patient at home"
                    fill
                    className="object-cover"
                    sizes="(max-width: 1024px) 0vw, 15vw"
                  />
                </div>
                <div className="relative aspect-[3/4] rounded-2xl overflow-hidden shadow-md" style={{ border: '1px solid var(--border)' }}>
                  <Image
                    src="/assets/images/WhatsApp_Image_2026-08-06_at_9.31.08_AM-1785989820464.jpeg"
                    alt="Physiotherapist conducting manual therapy treatment during home visit"
                    fill
                    className="object-cover"
                    sizes="(max-width: 1024px) 0vw, 15vw"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services List */}
        <section className="section-pad" style={{ backgroundColor: 'rgba(240,244,244,0.3)' }}>
          <div className="mx-auto max-w-6xl px-6">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2 mb-10">
              {categories.map((cat) =>
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2 rounded-full text-sm font-bold transition-all duration-200 ${
                activeCategory === cat ?
                'text-white shadow-md' :
                'hover:opacity-80'}`
                }
                style={{
                  backgroundColor: activeCategory === cat ? 'var(--primary)' : 'var(--card)',
                  color: activeCategory === cat ? 'white' : 'var(--foreground)',
                  border: activeCategory === cat ? 'none' : '1.5px solid var(--border)'
                }}>
                
                  {cat}
                </button>
              )}
            </div>

            {/* Services Grid */}
            <div className="space-y-6">
              {filteredServices.map((service) =>
              <div key={service.id} className="bento-card group">
                  <div className="flex flex-col lg:flex-row gap-6">
                    {/* Image */}
                    <div className="relative w-full lg:w-64 h-48 lg:h-auto rounded-2xl overflow-hidden shrink-0">
                      <Image
                      src={service.image}
                      alt={service.imageAlt}
                      fill
                      className="object-cover transition-transform duration-500 group-hover:scale-105"
                      sizes="(max-width: 1024px) 100vw, 256px" />
                    
                      {service.badge &&
                    <div className="absolute top-3 left-3">
                          <span className="service-tag">{service.badge}</span>
                        </div>
                    }
                    </div>

                    {/* Content */}
                    <div className="flex-1 flex flex-col">
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-4 mb-3">
                          <div>
                            <h3 className="text-xl font-display font-bold" style={{ color: 'var(--foreground)' }}>{service.name}</h3>
                            <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>{service.shortDesc}</p>
                          </div>
                        </div>

                        <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--muted-foreground)' }}>{service.longDesc}</p>

                        {/* Treatments */}
                        <div className="flex flex-wrap gap-2 mb-4">
                          {service.treatments.map((t) =>
                        <span
                          key={t}
                          className="text-xs px-2.5 py-1 rounded-full font-semibold uppercase tracking-wide"
                          style={{ backgroundColor: 'var(--secondary)', color: 'var(--primary)', border: '1px solid rgba(26,107,107,0.15)' }}>
                          
                              {t}
                            </span>
                        )}
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="flex justify-end pt-4" style={{ borderTop: '1px solid var(--border)' }}>
                        <Link href="/contact" className="btn-primary text-sm px-5 py-2.5">
                          Book This Service
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="14" height="14">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                          </svg>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Trust Badges */}
        <section className="py-12" style={{ backgroundColor: 'var(--background)', borderTop: '1px solid var(--border)' }}>
          <div className="mx-auto max-w-6xl px-6">
            <div className="flex flex-wrap items-center justify-center gap-8">
              {[
              { label: 'CMT Certified therapists' },
              { label: 'MIAP Registered' },
              { label: 'Free rescheduling 24hrs notice' },
              { label: 'Progress reports on request' }].
              map((item, i) =>
              <React.Fragment key={item.label}>
                  {i > 0 && <div className="h-8 w-px hidden sm:block" style={{ backgroundColor: 'var(--border)' }}></div>}
                  <div className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16" style={{ color: 'var(--primary)' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z" />
                    </svg>
                    <span className="text-sm font-medium" style={{ color: 'var(--muted-foreground)' }}>{item.label}</span>
                  </div>
                </React.Fragment>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </>);

}